import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/api';
import { UserRole, UserStatus } from '@shared/schema';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Button,
  buttonVariants
} from '@/components/ui/button';
import { 
  User, 
  Search, 
  CheckCircle, 
  XCircle, 
  Clock, 
  MoreHorizontal, 
  Check, 
  X, 
  Eye,
  Trash2,
  UserCheck,
  Phone,
  MessageSquare
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export default function AdminUsersPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [userToReject, setUserToReject] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Fetch all users
  useEffect(() => {
    console.log("Current logged in user:", user);
    console.log("Is user admin or super admin?", !!user && (user?.role === UserRole.ADMIN));
  }, [user]);
  
  // Directly fetch database users using SQL API
  const { data: usersData, isLoading, error: userFetchError, refetch } = useQuery({
    queryKey: ['admin/users-direct'],
    enabled: !!user && (user.role === UserRole.ADMIN),
    queryFn: async () => {
      try {
        const data = await apiRequest('GET', 'admin/users-direct');
        return data;
      } catch (error) {
        console.error("Error fetching users:", error);
        throw error;
      }
    },
    retry: 3,
    staleTime: 5000,
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });
  
  // Process the user data - handle both array and object format
  const users = React.useMemo(() => {
    console.log("Processing users data:", usersData);
    
    if (!usersData) return [];
    
    // If response is array, use it directly
    if (Array.isArray(usersData)) {
      console.log("Users data is an array with", usersData.length, "items");
      return usersData;
    }
    
    // If response has users array property
    if (usersData.users && Array.isArray(usersData.users)) {
      console.log("Users data has users array with", usersData.users.length, "items");
      return usersData.users;
    }
    
    console.log("Users data format unknown, converting to array:", usersData);
    // Otherwise convert to array if single object, or empty array
    return typeof usersData === 'object' ? [usersData] : [];
  }, [usersData]);
  
  useEffect(() => {
    if (userFetchError) {
      toast({
        title: 'Error fetching users',
        description: 'Check console for details',
        variant: 'destructive',
      });
    }
  }, [userFetchError, toast]);
  
  // Approve user mutation
  const approveMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiRequest('PUT', `admin/users/${userId}/approve`, {});
      // return res.json();
      return res;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'User approved successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['admin/users-direct'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to approve user: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  // Reject user mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: number; reason: string }) => {
      const res = await apiRequest('PUT', `admin/users/${userId}/reject`, { rejectionReason: reason });
      // return res.json();
      return res;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'User rejected successfully',
      });
      setRejectDialogOpen(false);
      setRejectionReason('');
      queryClient.invalidateQueries({ queryKey: ['admin/users-direct'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to reject user: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  // Update user role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: string }) => {
      const res = await apiRequest('PUT', `admin/users/${userId}/role`, { role });
      // return res.json();
      return res;
    },
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: `User role updated to ${data.user?.role || data.role || 'new role'}`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin/users-direct'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to update user role: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  // Update user status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ userId, status }: { userId: number; status: string }) => {
      const res = await apiRequest('PUT', `admin/users/${userId}/status`, { status });
      // return res.json();
      return res;
    },
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: `User status updated to ${data.user?.status || data.status || 'new status'}`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin/users-direct'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to update user status: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  const handleApprove = (userId: number) => {
    if (confirm('Are you sure you want to approve this user?')) {
      approveMutation.mutate(userId);
    }
  };
  
  const handleRejectClick = (user: any) => {
    setUserToReject(user);
    setRejectDialogOpen(true);
  };
  
  const handleRejectConfirm = () => {
    if (!userToReject) return;
    
    if (!rejectionReason.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please provide a rejection reason',
        variant: 'destructive',
      });
      return;
    }
    
    rejectMutation.mutate({
      userId: userToReject.id,
      reason: rejectionReason
    });
  };
  
  // Filter users based on search query, status filter, and role filter
  const filteredUsers = users.filter((user: any) => {
    const matchesSearch = 
      user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.fullName?.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesStatusFilter = 
      statusFilter === 'all' || 
      user.status.toLowerCase() === statusFilter.toLowerCase();
      
    const matchesRoleFilter = 
      roleFilter === 'all' || 
      user.role.toLowerCase() === roleFilter.toLowerCase();
      
    return matchesSearch && matchesStatusFilter && matchesRoleFilter;
  });
  
  // Status badge component
  const getStatusBadge = (status: UserStatus) => {
    switch (status.toLowerCase()) {
      case UserStatus.APPROVED.toLowerCase():
        return <Badge className="bg-green-500 hover:bg-green-600">Approved</Badge>;
      case UserStatus.PENDING.toLowerCase():
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Pending</Badge>;
      case UserStatus.REJECTED.toLowerCase():
        return <Badge className="bg-red-500 hover:bg-red-600">Rejected</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };
  
  // Role badge component
  const getRoleBadge = (role: UserRole) => {
    switch (role?.toLowerCase()) {
      case UserRole.ADMIN.toLowerCase():
        return <Badge className="bg-purple-500 hover:bg-purple-600">Admin</Badge>;
      case UserRole.OWNER.toLowerCase():
        return <Badge className="bg-blue-500 hover:bg-blue-600">Owner</Badge>;
      case UserRole.AGENT.toLowerCase():
        return <Badge className="bg-cyan-500 hover:bg-cyan-600">Agent</Badge>;
      case UserRole.MANAGER.toLowerCase():
        return <Badge className="bg-indigo-500 hover:bg-indigo-600">Manager</Badge>;
      // Guest user case
      case 'guest':
        return <Badge className="bg-gray-500 hover:bg-gray-600">Guest</Badge>;
      // Regular user case
      case 'user':
        return <Badge className="bg-gray-500 hover:bg-gray-600">User</Badge>;
      default:
        return <Badge className="bg-red-500 hover:bg-red-600">{role || "Unknown"}</Badge>;
    }
  };
  
  // Get counts for each status
  const statusCounts = {
    all: users.length,
    [UserStatus.APPROVED.toLowerCase()]: users.filter((u: any) => u.status.toLowerCase() === UserStatus.APPROVED.toLowerCase()).length,
    [UserStatus.PENDING.toLowerCase()]: users.filter((u: any) => u.status.toLowerCase() === UserStatus.PENDING.toLowerCase()).length,
    [UserStatus.REJECTED.toLowerCase()]: users.filter((u: any) => u.status.toLowerCase() === UserStatus.REJECTED.toLowerCase()).length,
  };
  
  // Get counts for each role
  const roleCounts = {
    all: users.length,
    [UserRole.ADMIN.toLowerCase()]: users.filter((u: any) => u.role.toLowerCase() === UserRole.ADMIN.toLowerCase()).length,
    [UserRole.OWNER.toLowerCase()]: users.filter((u: any) => u.role.toLowerCase() === UserRole.OWNER.toLowerCase()).length,
    [UserRole.AGENT.toLowerCase()]: users.filter((u: any) => u.role.toLowerCase() === UserRole.AGENT.toLowerCase()).length,
    'user': users.filter((u: any) => u.role.toLowerCase() === 'user').length,
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">User Management</h1>
        <p className="text-muted-foreground">Manage user accounts, approvals, and permissions</p>
      </div>
      
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
            <Select 
              value={statusFilter} 
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses ({statusCounts.all})</SelectItem>
                <SelectItem value={UserStatus.PENDING.toLowerCase()}>Pending ({statusCounts[UserStatus.PENDING.toLowerCase()]})</SelectItem>
                <SelectItem value={UserStatus.APPROVED.toLowerCase()}>Approved ({statusCounts[UserStatus.APPROVED.toLowerCase()]})</SelectItem>
                <SelectItem value={UserStatus.REJECTED.toLowerCase()}>Rejected ({statusCounts[UserStatus.REJECTED.toLowerCase()]})</SelectItem>
              </SelectContent>
            </Select>
            
            <Select 
              value={roleFilter} 
              onValueChange={setRoleFilter}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles ({roleCounts.all})</SelectItem>
                <SelectItem value={UserRole.ADMIN.toLowerCase()}>Admin ({roleCounts[UserRole.ADMIN.toLowerCase()]})</SelectItem>
                <SelectItem value={UserRole.OWNER.toLowerCase()}>Property Owner ({roleCounts[UserRole.OWNER.toLowerCase()]})</SelectItem>
                <SelectItem value={UserRole.AGENT.toLowerCase()}>Agent ({roleCounts[UserRole.AGENT.toLowerCase()]})</SelectItem>
                <SelectItem value="user">Regular User ({roleCounts['user']})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Tabs defaultValue="all" onValueChange={setStatusFilter} value={statusFilter}>
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="all" className="relative">
              All
              <span className="ml-1 text-xs">({statusCounts.all})</span>
            </TabsTrigger>
            <TabsTrigger value={UserStatus.PENDING.toLowerCase()} className="relative">
              <Clock className="w-4 h-4 mr-1" />
              Pending
              <span className="ml-1 text-xs">({statusCounts[UserStatus.PENDING.toLowerCase()]})</span>
            </TabsTrigger>
            <TabsTrigger value={UserStatus.APPROVED.toLowerCase()} className="relative">
              <CheckCircle className="w-4 h-4 mr-1" />
              Approved
              <span className="ml-1 text-xs">({statusCounts[UserStatus.APPROVED.toLowerCase()]})</span>
            </TabsTrigger>
            <TabsTrigger value={UserStatus.REJECTED.toLowerCase()} className="relative">
              <XCircle className="w-4 h-4 mr-1" />
              Rejected
              <span className="ml-1 text-xs">({statusCounts[UserStatus.REJECTED.toLowerCase()]})</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" aria-label="Loading" />
        </div>
      ) : filteredUsers.length > 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">User</TableHead>
                <TableHead className="whitespace-nowrap">Role</TableHead>
                <TableHead className="whitespace-nowrap">Email</TableHead>
                <TableHead className="whitespace-nowrap">Phone</TableHead>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <TableHead className="whitespace-nowrap">Registered</TableHead>
                <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user: any) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.fullName || user.username}</span>
                        <span className="text-xs text-muted-foreground">@{user.username}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getRoleBadge(user.role)}
                      
                      {/* Edit Button for Role Change */}
                      {(
                        <Select
                          value={user.role.toLowerCase()}
                          onValueChange={(newRole) => {
                            if (newRole !== user.role.toLowerCase() && 
                                confirm(`Change to ${newRole}?`)) {
                              updateRoleMutation.mutate({ 
                                userId: user.id,
                                role: newRole
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="h-7 w-7 p-0 ml-1">
                            <div className="flex justify-center items-center">
                              <MoreHorizontal className="h-4 w-4" />
                            </div>
                          </SelectTrigger>
                          <SelectContent align="end">
                            {Object.values(UserRole).map((role) => (
                              <SelectItem key={role} value={role.toLowerCase()}>{role}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate">{user.email}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {user.phone ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{user.phone}</span>
                        <div className="flex gap-1">
                          <a 
                            href={`tel:${user.phone}`} 
                            className={buttonVariants({
                              variant: "ghost",
                              size: "icon",
                              className: "h-7 w-7 bg-green-50 hover:bg-green-100"
                            })}
                            title="Call user"
                          >
                            <Phone className="h-3.5 w-3.5 text-green-600" />
                          </a>
                          <a 
                            href={`sms:${user.phone}`}
                            className={buttonVariants({
                              variant: "ghost",
                              size: "icon",
                              className: "h-7 w-7 bg-blue-50 hover:bg-blue-100"
                            })}
                            title="Message user"
                          >
                            <MessageSquare className="h-3.5 w-3.5 text-blue-600" />
                          </a>
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(user.status)}
                      
                      {/* Edit Button for Status Change */}
                      { (
                        <Select
                          value={user.status.toLowerCase()}
                          onValueChange={(newStatus) => {
                            if (newStatus !== user.status.toLowerCase() && 
                                confirm(`Change to ${newStatus}?`)) {
                              updateStatusMutation.mutate({ 
                                userId: user.id,
                                status: newStatus
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="h-7 w-7 p-0 ml-1">
                            <div className="flex justify-center items-center">
                              <MoreHorizontal className="h-4 w-4" />
                            </div>
                          </SelectTrigger>
                          <SelectContent align="end">
                            {Object.values(UserStatus).map((status) => (
                              <SelectItem key={status} value={status.toLowerCase()}>{status}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {/* View Button */}
                      <div>
                        <a 
                          href={`/admin/users/${user.id}`}
                          className={buttonVariants({
                            variant: "ghost",
                            size: "icon",
                            className: "bg-blue-50 hover:bg-blue-100"
                          })}
                          title="View user details"
                        >
                          <Eye className="h-4 w-4 text-blue-600" />
                        </a>
                      </div>
                      
                      {/* Approve Button - only for pending users */}
                      {user.status.toLowerCase() === UserStatus.PENDING.toLowerCase() && (
                        <Button 
                          variant="ghost"
                          size="icon"
                          title="Approve user"
                          onClick={() => handleApprove(user.id)}
                          className="bg-green-50 hover:bg-green-100"
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      
                      {/* Reject Button - only for pending users */}
                      {user.status.toLowerCase() === UserStatus.PENDING.toLowerCase() && (
                        <Button 
                          variant="ghost"
                          size="icon"
                          title="Reject user"
                          onClick={() => handleRejectClick(user)}
                          className="bg-red-50 hover:bg-red-100"
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                      
                      {/* Delete Button - always visible */}
                      <Button 
                        variant="ghost"
                        size="icon"
                        title="Delete user"
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete "${user.fullName || user.username}"? This action cannot be undone.`)) {
                            // Call API to delete user
                            apiRequest('DELETE', `admin/users/${user.id}`);
                            toast({
                              title: "User Deleted",
                              description: "The user has been permanently removed from the system.",
                            });
                            queryClient.invalidateQueries({ queryKey: ['admin/users-direct'] });
                          }
                        }}
                        className="bg-red-50 hover:bg-red-100"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-16 bg-muted/30 rounded-lg">
          <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-bold mb-2">No users found</h3>
          <p className="text-muted-foreground mb-6">No users match your current search and filters</p>
          <Button variant="outline" onClick={() => {
            setSearchQuery('');
            setStatusFilter('all');
            setRoleFilter('all');
          }}>Clear Filters</Button>
        </div>
      )}
      
      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject User</DialogTitle>
            <DialogDescription>
              Provide a reason for rejection. This will be visible to the user.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h4 className="font-medium">{userToReject?.fullName || userToReject?.username}</h4>
                <p className="text-sm text-muted-foreground">{userToReject?.email}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason</Label>
              <Textarea 
                id="rejection-reason" 
                placeholder="Explain why this user is being rejected..."
                rows={4}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRejectDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}